import { FIREBALL, LAVA, ORB, SHOCKWAVE, TELEPORT, WORLD } from './constants';
import type { FireballState, PlayerState, Vec } from './types';

export const len = (v: Vec): number => Math.hypot(v.x, v.y);

export function normalize(v: Vec): Vec {
  const l = len(v);
  return l === 0 ? { x: 0, y: 0 } : { x: v.x / l, y: v.y / l };
}

/** Clamp a vector's length to at most `max`. */
export function clampLen(v: Vec, max: number): Vec {
  const l = len(v);
  return l > max ? { x: (v.x / l) * max, y: (v.y / l) * max } : { x: v.x, y: v.y };
}

export const distFromCenter = (x: number, y: number): number => Math.hypot(x - WORLD.cx, y - WORLD.cy);

export const inLava = (x: number, y: number, islandRadius: number): boolean =>
  distFromCenter(x, y) > islandRadius;

/**
 * Advance one orb by `dt` seconds from its current input. Mutates `p`.
 * Movement velocity and knockback velocity are integrated separately so a
 * hit never cancels the player's input, it just adds a slide on top.
 *
 * Pure and deterministic: the server runs it once per queued input, and the
 * client runs the exact same function to predict its own orb.
 */
export function stepPlayer(p: PlayerState, move: Vec, islandRadius: number, dt: number, applyDamage = true): void {
  const lava = inLava(p.x, p.y, islandRadius);
  const m = clampLen(move, 1);
  const moving = m.x !== 0 || m.y !== 0;

  // Walk velocity chases the target directly (no drag while moving, no
  // momentum after release). Lava: slower target, sluggish response.
  const maxSpeed = lava ? ORB.maxSpeed * LAVA.speedPenalty : ORB.maxSpeed;
  const tau = (moving ? ORB.accelTime : ORB.stopTime) / (lava ? LAVA.accelPenalty : 1);
  const k = 1 - Math.exp(-dt / tau);
  p.vx += (m.x * maxSpeed - p.vx) * k;
  p.vy += (m.y * maxSpeed - p.vy) * k;

  const kbFriction = Math.pow(0.1, dt * ORB.knockbackFriction);
  p.kx *= kbFriction;
  p.ky *= kbFriction;

  if (Math.abs(p.vx) < ORB.stopThreshold) p.vx = 0;
  if (Math.abs(p.vy) < ORB.stopThreshold) p.vy = 0;
  if (Math.abs(p.kx) < ORB.stopThreshold) p.kx = 0;
  if (Math.abs(p.ky) < ORB.stopThreshold) p.ky = 0;

  p.x += (p.vx + p.kx) * dt;
  p.y += (p.vy + p.ky) * dt;

  // Soft world edge: bounce back with half the energy.
  const r = ORB.radius;
  if (p.x < r) {
    p.x = r;
    p.vx = Math.abs(p.vx) * 0.5;
    p.kx = Math.abs(p.kx) * 0.5;
  } else if (p.x > WORLD.size - r) {
    p.x = WORLD.size - r;
    p.vx = -Math.abs(p.vx) * 0.5;
    p.kx = -Math.abs(p.kx) * 0.5;
  }
  if (p.y < r) {
    p.y = r;
    p.vy = Math.abs(p.vy) * 0.5;
    p.ky = Math.abs(p.ky) * 0.5;
  } else if (p.y > WORLD.size - r) {
    p.y = WORLD.size - r;
    p.vy = -Math.abs(p.vy) * 0.5;
    p.ky = -Math.abs(p.ky) * 0.5;
  }

  if (lava && applyDamage) p.hp = Math.max(0, p.hp - LAVA.damagePerSec * dt);
}

export function stepFireball(f: FireballState, dt: number): void {
  const d = FIREBALL.speed * dt;
  f.x += f.dx * d;
  f.y += f.dy * d;
  f.left -= d;
}

export const fireballSpent = (f: FireballState): boolean => f.left <= 0;

export function fireballOutOfWorld(f: FireballState): boolean {
  const m = FIREBALL.cullMargin;
  return f.x < -m || f.y < -m || f.x > WORLD.size + m || f.y > WORLD.size + m;
}

export function fireballHitsOrb(f: FireballState, p: PlayerState): boolean {
  return Math.hypot(p.x - f.x, p.y - f.y) < ORB.radius + FIREBALL.radius;
}

export interface Knockback {
  dir: Vec;
  /** 0.6–1.0; glancing hits are deliberately strong so orbs get flung sideways */
  force: number;
}

/**
 * Ported from the prototype: blends the fireball's travel direction with the
 * impact normal and boosts the perpendicular component, so hits feel like
 * being shoved off-balance rather than pushed straight back.
 */
export function knockbackFor(f: FireballState, p: PlayerState): Knockback {
  const impact = normalize({ x: p.x - f.x, y: p.y - f.y });
  const travel = { x: f.dx, y: f.dy };
  const dot = travel.x * impact.x + travel.y * impact.y;
  const perp = { x: -travel.y, y: travel.x };
  const perpDot = travel.x * -impact.y + travel.y * impact.x;

  const directWeight = Math.abs(dot);
  const perpWeight = Math.abs(perpDot) * (1 - Math.abs(dot)) * 2;

  let kx = impact.x * directWeight + perp.x * perpWeight;
  let ky = impact.y * directWeight + perp.y * perpWeight;

  if (Math.abs(perpDot) > 0.3) {
    const extra = Math.max(0.3, perpWeight);
    kx += perp.x * extra;
    ky += perp.y * extra;
  }

  // Always at least somewhat away from the fireball.
  if (kx * impact.x + ky * impact.y < 0.3) {
    kx = kx * 0.3 + impact.x * 0.7;
    ky = ky * 0.3 + impact.y * 0.7;
  }

  return {
    dir: normalize({ x: kx, y: ky }),
    force: Math.max(0.6, Math.abs(dot) * 0.7 + Math.abs(perpDot) * 0.5),
  };
}

/** Knockback grows with damage already taken: ×1 at full hp, ×2 at the brink (Warlock's kb_points). */
export const knockbackScale = (p: PlayerState): number =>
  1 + ((ORB.maxHp - Math.max(0, p.hp)) / ORB.maxHp) * FIREBALL.knockbackDamageScale;

export function applyFireballHit(f: FireballState, p: PlayerState): Knockback {
  const kb = knockbackFor(f, p);
  const v = FIREBALL.knockbackVelocity * kb.force * knockbackScale(p);
  p.kx += kb.dir.x * v;
  p.ky += kb.dir.y * v;
  p.hp = Math.max(0, p.hp - FIREBALL.baseDamage * kb.force);
  return kb;
}

/**
 * Elastic-ish collision between two orbs. Separates them and exchanges
 * velocity along the contact normal into their knockback channels.
 * Returns the impulse magnitude (0 if they weren't touching).
 */
export function resolveOrbCollision(a: PlayerState, b: PlayerState): number {
  const nx0 = b.x - a.x;
  const ny0 = b.y - a.y;
  const dist = Math.hypot(nx0, ny0);
  const minDist = ORB.radius * 2;
  if (dist >= minDist || dist === 0) return 0;

  const nx = nx0 / dist;
  const ny = ny0 / dist;

  // Push apart equally.
  const overlap = minDist - dist;
  a.x -= (nx * overlap) / 2;
  a.y -= (ny * overlap) / 2;
  b.x += (nx * overlap) / 2;
  b.y += (ny * overlap) / 2;

  const avx = a.vx + a.kx;
  const avy = a.vy + a.ky;
  const bvx = b.vx + b.kx;
  const bvy = b.vy + b.ky;
  const relN = (bvx - avx) * nx + (bvy - avy) * ny;
  if (relN > 0) return 0; // already separating

  let j = (-(1 + ORB.restitution) * relN) / 2;
  j = Math.max(j, ORB.minBumpImpulse);

  a.kx -= nx * j;
  a.ky -= ny * j;
  b.kx += nx * j;
  b.ky += ny * j;
  return j;
}

/**
 * Warlock's teleport: jump toward `target`, at most TELEPORT.range away
 * (aim past the range and you travel the full range that way), keeping a
 * fraction of your knockback so a slide toward the lava isn't cancelled —
 * only relocated. Cooldown is the caller's business. Returns where you left.
 */
export function applyTeleport(p: PlayerState, target: Vec): Vec {
  const from = { x: p.x, y: p.y };
  const dx = target.x - p.x;
  const dy = target.y - p.y;
  const d = Math.hypot(dx, dy);
  if (d > 0) {
    const t = Math.min(1, TELEPORT.range / d);
    p.x += dx * t;
    p.y += dy * t;
  }
  const r = ORB.radius;
  p.x = Math.min(WORLD.size - r, Math.max(r, p.x));
  p.y = Math.min(WORLD.size - r, Math.max(r, p.y));
  p.kx *= TELEPORT.keepMomentum;
  p.ky *= TELEPORT.keepMomentum;
  return from;
}

/**
 * Shockwave: shove `victim` straight away from `origin` if it's within
 * SHOCKWAVE.radius. Strongest at the centre, tapering to `edgeFactor` at
 * the rim; the damage ramp applies like any knockback. Returns true if hit.
 */
export function applyShockwave(origin: Vec, victim: PlayerState): boolean {
  let dx = victim.x - origin.x;
  let dy = victim.y - origin.y;
  const d = Math.hypot(dx, dy);
  if (d > SHOCKWAVE.radius) return false;
  if (d === 0) {
    dx = 1;
    dy = 0;
  } else {
    dx /= d;
    dy /= d;
  }
  const falloff = 1 - (1 - SHOCKWAVE.edgeFactor) * (d / SHOCKWAVE.radius);
  const v = SHOCKWAVE.knockbackVelocity * falloff * knockbackScale(victim);
  victim.kx += dx * v;
  victim.ky += dy * v;
  return true;
}
